/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { useMemo } from "react";

import { useAuth, useWatchForm } from "hooks";
import sortBy from "lodash/sortBy";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { PayrollCycle } from "services/PayrollCycle";
import { Status } from "services/types";
import { getLabelPayrollCycle, getLanContent } from "utils";

import {
  paTemplateDetailFieldMeService,
  paTemplateListByUserService,
} from "./services";
import { PATemplate } from "./types";

interface PayrollCycleParent {
  id: string;
  payrollCycle: PayrollCycle;
}

export const useListPayrollPATemplateByUser = ({
  methods,
  keyPATemplate,
  keyPATemplatePeriod,
  enabled = true,
  keyQuery,
  defaultPATemplateId,
  enabledField,
  onSuccess,
  onSetField,
}: {
  methods?: ReturnType<typeof useForm>;
  keyPATemplate?: string;
  keyPATemplatePeriod?: string;
  enabled?: boolean;
  keyQuery?: string;
  defaultPATemplateId?: string;
  enabledField?: boolean;
  onSuccess?: (e: {
    label: string;
    value: string;
    original: PATemplate;
    cycleId?: string;
    templatePeriod: number;
  }) => void;
  onSetField?: (name: string, value: any) => void;
}) => {
  const cycle = methods?.watch("cycleId");
  const { data } = useAuth();
  const templateId: any =
    methods?.watch(keyPATemplate as never) ||
    ({} as { value: string; original: PATemplate });
  const templatePeriodId: any =
    methods?.watch(keyPATemplatePeriod as never) || "";

  const templateList = useQuery({
    queryKey: ["templateListService", keyQuery],
    enabled,
    queryFn: () =>
      paTemplateListByUserService({
        status: Status.Active,
        page: 1 as never,
        limit: 20,
      }),
    onSuccess: (e) => {
      if (onSuccess && e?.data?.items) {
        const defaultPATemplateIdObject =
          e?.data?.items?.find((i) => i.id === defaultPATemplateId) ||
          e?.data?.items?.[0];
        let cycles = defaultPATemplateIdObject?.productivityTemplateCycles?.map(
          (i) => i?.payrollCycle
        );
        cycles = sortBy(cycles || [], "period");
        const templatePeriods = sortBy(
          defaultPATemplateIdObject?.productivityTemplatePeriods || [],
          "period"
        );
        onSuccess({
          label: getLanContent(defaultPATemplateIdObject, "name"),
          value: defaultPATemplateIdObject?.id,
          original: defaultPATemplateIdObject,
          templatePeriod: +(templatePeriods?.[0]?.period || 0),
          cycleId: cycles?.[0]?.id || "",
        });
      }
    },
  });

  const templateFieldQuery = useQuery({
    queryKey: ["templateDetailFieldMeService", templateId?.original?.id],
    enabled: !!templateId?.original?.id && enabledField,
    queryFn: () =>
      paTemplateDetailFieldMeService(templateId?.original?.id as never),
  });

  useWatchForm({
    methods,
    callback: ({ name, type, value }) => {
      if (type === "change" && name === keyPATemplate) {
        const currentPATemplate = value?.[keyPATemplate as never];
        const templatePeriods = sortBy(
          currentPATemplate?.original?.productivityTemplatePeriods || [],
          "period"
        );
        const cycles = sortBy(
          currentPATemplate?.original?.productivityTemplateCycles?.map(
            (i: any) => i?.payrollCycle
          ) || [],
          "period"
        );
        if (typeof onSetField === "function") {
          onSetField(
            keyPATemplatePeriod || "",
            templatePeriods?.[0]?.period || undefined
          );
        }
        methods?.setValue(
          keyPATemplatePeriod || "",
          templatePeriods?.[0]?.period || undefined
        );
        methods?.setValue("cycleId", cycles?.[0]?.id || undefined);
      }
    },
  });

  const templatePeriods = useMemo(() => {
    if (templateId?.original?.productivityTemplatePeriods) {
      return (
        sortBy(
          templateId?.original?.productivityTemplatePeriods || [],
          "period"
        ).map((e: { period: string }) => ({
          label: e.period,
          value: e.period,
        })) || []
      );
    }
    return [];
  }, [templateId?.original?.productivityTemplatePeriods]);

  const templatePeriod = useMemo(() => {
    return templateId?.original?.productivityTemplatePeriods?.find(
      (e: { period: string }) => +(e.period || 0) === +(templatePeriodId || "")
    );
  }, [templateId?.original?.productivityTemplatePeriods, templatePeriodId]);

  const cycles = useMemo(() => {
    return (
      templateId?.original?.productivityTemplateCycles?.map(
        (i: PayrollCycleParent) => i?.payrollCycle
      ) || []
    );
  }, [templateId?.original?.productivityTemplateCycles]);
  const cycleOptions = useMemo(() => {
    return cycles.map((i: PayrollCycle) => ({
      label: getLabelPayrollCycle(i as never),
      value: i?.id,
    }));
  }, [cycles]);
  const cycleObject = useMemo(() => {
    const currentObject = cycles?.find((i: PayrollCycle) => i?.id === cycle);
    return currentObject || {};
  }, [cycle, cycles]);

  return {
    data: templateList?.data?.data?.items,
    empCode: data?.employeeCode,
    isFetched: templateList.isFetched,
    templatePeriods,
    templatePeriod,
    templateDetail: templateFieldQuery?.data?.data,

    periodData: {
      templatePeriod: cycleObject?.period || undefined,
      productivityTemplateId: templateId?.value || undefined,
      cycleId: cycle || undefined,
    },
    templateId,
    periodState: {
      cycleOptions,
      cycles,
      cycleObject,
    },
  };
};
